import { motion } from 'framer-motion'
import { CheckCircle, ArrowRight } from 'lucide-react'
import { coachingVsTherapy } from '@/content/site'
import { CalendlyBtn } from '@/components/ui/calendly-btn'

export default function CoachingVsTherapyPage() {
  return (
    <div className="bg-white dark:bg-slate-950 min-h-screen">

      {/* Header */}
      <div className="bg-slate-900 pt-28 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="text-blue-400 text-sm font-semibold tracking-widest uppercase mb-3">
            Understanding Your Options
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="text-4xl sm:text-5xl font-bold text-white mb-4">
            {coachingVsTherapy.heading}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="text-slate-400 text-lg max-w-2xl italic">
            {coachingVsTherapy.tagline}
          </motion.p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20">

        {/* Intro */}
        <div className="max-w-3xl mb-16">
          {coachingVsTherapy.intro.map((para, i) => (
            <motion.p key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="text-slate-600 dark:text-slate-300 leading-relaxed mb-4 text-lg">
              {para}
            </motion.p>
          ))}
        </div>

        {/* Comparison table */}
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="mb-16 overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-700">
          <div className="grid grid-cols-3 bg-slate-900 text-white">
            <div className="p-4 text-sm font-semibold text-slate-400 uppercase tracking-widest">Aspect</div>
            <div className="p-4 text-sm font-semibold text-slate-300 border-l border-slate-700">Therapy</div>
            <div className="p-4 text-sm font-semibold text-blue-400 border-l border-slate-700">Coaching</div>
          </div>
          {coachingVsTherapy.comparison.map((row, i) => (
            <div key={i}
              className={`grid grid-cols-3 border-t border-slate-100 dark:border-slate-800 ${i % 2 === 0 ? 'bg-white dark:bg-slate-900' : 'bg-slate-50 dark:bg-slate-800/50'}`}>
              <div className="p-4 text-sm font-semibold text-slate-700 dark:text-slate-300">{row.aspect}</div>
              <div className="p-4 text-sm text-slate-500 dark:text-slate-400 border-l border-slate-100 dark:border-slate-800">{row.therapy}</div>
              <div className="p-4 text-sm text-slate-700 dark:text-slate-200 border-l border-slate-100 dark:border-slate-800 font-medium">{row.coaching}</div>
            </div>
          ))}
        </motion.div>

        {/* Where coaching picks up */}
        <div className="grid lg:grid-cols-2 gap-10 mb-16">
          <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
            className="bg-slate-50 dark:bg-slate-800 rounded-2xl p-8">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
              <ArrowRight size={18} className="text-blue-500" /> Where Coaching Picks Up
            </h2>
            <p className="text-slate-600 dark:text-slate-300 leading-relaxed text-sm">
              {coachingVsTherapy.whereCoachingPicksUp}
            </p>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}
            className="bg-blue-50 dark:bg-blue-900/20 rounded-2xl p-8 border border-blue-100 dark:border-blue-800">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
              <CheckCircle size={18} className="text-blue-500" /> IFS in Coaching
            </h2>
            <p className="text-slate-600 dark:text-slate-300 leading-relaxed text-sm">
              {coachingVsTherapy.ifsInCoaching}
            </p>
          </motion.div>
        </div>

        {/* CTA block */}
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="bg-slate-900 rounded-3xl p-10 text-center">
          <p className="text-blue-400 text-xs font-semibold uppercase tracking-widest mb-3">Not sure which path is right?</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">{coachingVsTherapy.cta}</h2>
          <p className="text-slate-400 max-w-xl mx-auto mb-8 leading-relaxed">{coachingVsTherapy.ctaBody}</p>
          <CalendlyBtn size="lg" className="bg-blue-600 hover:bg-blue-700 text-white border-transparent rounded-full hover:scale-105 hover:shadow-lg hover:shadow-blue-600/25 transition-all">
            Book a Free Consultation
          </CalendlyBtn>
        </motion.div>
      </div>
    </div>
  )
}
